/**
 * The renderer, the scene, and the lifecycle of the models on display.
 *
 * There is one scene and one render loop for both presentation modes. Desktop and VR
 * differ in which models they show, how they transform them, and where they put the
 * camera, so they are swappable strategies over this shared state rather than separate
 * viewers.
 *
 * Models are held in a map keyed by their library id, and each one owns a `Group` of its
 * own that nothing else transforms. That is what lets a headset user pick one model up
 * without the others following it: there is no shared parent to move. The desktop shows
 * exactly one of them — real scale means nothing on a monitor, so it normalises whichever
 * model is selected — while VR shows the whole room.
 */

import {
  ACESFilmicToneMapping,
  Box3,
  BufferGeometry,
  Color,
  DirectionalLight,
  Group,
  HemisphereLight,
  Mesh,
  MeshStandardMaterial,
  type Object3D,
  PCFSoftShadowMap,
  PerspectiveCamera,
  Scene,
  Sphere,
  Texture,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';

import { PLYLoader } from 'three/addons/loaders/PLYLoader.js';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';

import { DEFAULT_COLOUR } from '../domain/colour.js';
import { hasFileColours, kindOf } from '../domain/modelFile.js';
import { computeCenteringOffset } from '../domain/scaling.js';
import type { Theme } from '../domain/theme.js';
import type { Bounds, ModelId, ModelStats } from '../domain/types.js';
import { createFloor } from './floor.js';
import { mergeParts } from './meshParts.js';
import { readPlyAlpha } from '../domain/ply.js';
import { applyPlyAlpha, correctColourScale } from './plyColours.js';
import { buildScene } from './sceneFile.js';
import { readToolpath } from './toolpath.js';
import { Sign, type SignInk } from './sign.js';

const CAMERA_FOV_DEGREES = 50;

/**
 * The fill light's two colours: what a surface facing up sees, and what one facing down
 * sees.
 *
 * A hemisphere light chooses between them by which way a surface points, so these are
 * independent — the ground colour reaches only the undersides, and lifting it does not
 * touch the lit top of a model at all.
 *
 * It was much darker, and near-black undersides were the consequence: a model resting on
 * the floor showed nothing at all underneath. That was tolerable while every surface was a
 * single colour, since there was nothing down there to see. It stopped being tolerable once
 * a file could carry a picture, because a 3MF quite reasonably puts one on the face it
 * stands on, and the whole feature was invisible from the one angle that mattered.
 *
 * Set a little under half the sky's brightness. High enough to read detail in shadow, and
 * far enough below the sky that a model still has a top and a bottom — matched, and the
 * form goes flat.
 */
const SKY_FILL = 0xdde7ee;
const GROUND_FILL = 0x79838f;

/**
 * How the room looks under each theme.
 *
 * Kept here rather than in the stylesheet, unlike the page's own colours: nothing in CSS
 * paints any of this, and a value that only ever reaches a shader has no business being
 * declared as though it styled an element.
 *
 * The light sky is a shade deeper than the stage the canvas sits in on a screen. On a
 * monitor the canvas is a panel with a room around it and matching that room is the whole
 * point; in a headset it is the entire visual field at arm's length, where the page's
 * near-white would be glare rather than daylight. This reads as an overcast sky, which is
 * what a bright room ought to look like from inside it.
 *
 * The dark values are what the room has always been, so choosing dark — or having it
 * chosen by a system set that way — leaves VR exactly as it was.
 */
interface RoomLook {
  readonly sky: number;
  readonly ink: SignInk;
}

const ROOM: Record<Theme, RoomLook> = {
  dark: { sky: 0x0e1114, ink: { colour: '#c6d3df', opacity: 0.5 } },
  light: { sky: 0xc8d4e0, ink: { colour: '#1e2733', opacity: 0.65 } },
};

/**
 * A presentation mode owns the camera and the model's transform for as long as it is
 * active. Exactly one is active at a time.
 */
export interface PresentationMode {
  enter(): void;
  exit(): void;
  /** Called once per rendered frame, before the draw. */
  update(deltaSeconds: number): void;
}

/**
 * One model in the scene.
 *
 * `root` is what gets transformed — by whichever mode is active, and by a controller
 * during a VR grab — and it belongs to this model alone. What is drawn is a child of it
 * rather than the thing moved directly, so that re-parenting to a hand and back leaves the
 * geometry untouched.
 */
export interface SceneModel {
  readonly id: ModelId;
  readonly root: Group;
  /**
   * What is drawn, and what carries the centering offset.
   *
   * A single mesh for most formats. glTF and 3MF keep the scene of separately shaded parts
   * their loader produced, so for those it is a group — which is why nothing here assumes
   * one mesh, and why recolouring and disposal both walk it.
   */
  readonly object: Object3D;
  /** The model's own bounds, in millimetres, for whatever has to place or frame it. */
  readonly bounds: Bounds;
}

export class SceneManager {
  readonly scene = new Scene();
  readonly camera: PerspectiveCamera;
  readonly renderer: WebGLRenderer;

  readonly floor = createFloor();
  readonly sign = new Sign(ROOM.dark.ink);

  /**
   * What the canvas shows behind the model on a screen, and inside a headset.
   *
   * Two colours rather than one because they are answering different questions. On a
   * screen the canvas is a panel among other panels and takes its colour from them, so
   * that the view is not a rectangle of some other shade in the middle of the page. The
   * sky in VR follows the same theme but is chosen for a headset rather than for a
   * monitor — see `ROOM` above.
   */
  private readonly pageBackground = new Color(0x0e1114);
  private readonly roomBackground = new Color(ROOM.dark.sky);

  private readonly keyLight: DirectionalLight;
  private readonly loader = new STLLoader();

  /** Every model currently built and in the scene, whether or not it is being shown. */
  private readonly models = new Map<ModelId, SceneModel>();

  private mode: PresentationMode | undefined;
  private lastFrameTime = 0;

  constructor(private readonly host: HTMLElement) {
    this.renderer = new WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.xr.enabled = true;
    host.appendChild(this.renderer.domElement);

    this.scene.background = this.pageBackground;

    this.camera = new PerspectiveCamera(CAMERA_FOV_DEGREES, 1, 0.01, 200);

    // Broad ambient fill so nothing goes to pure black, plus one directional key light
    // that actually casts a shadow — that shadow is most of what sells the model as
    // sitting on the floor rather than hovering in a void.
    const fill = new HemisphereLight(SKY_FILL, GROUND_FILL, 1.1);
    this.keyLight = new DirectionalLight(0xffffff, 2.2);
    this.keyLight.position.set(2, 4, 3);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.mapSize.set(2048, 2048);
    this.keyLight.shadow.bias = -0.0005;
    this.keyLight.shadow.normalBias = 0.02;

    this.scene.add(fill, this.keyLight, this.keyLight.target, this.floor, this.sign.mesh);

    this.setEnvironmentVisible(false);
    this.resize();
  }

  /** Every model built and in the scene, in the order they were loaded. */
  get loaded(): readonly SceneModel[] {
    return [...this.models.values()];
  }

  get(id: ModelId): SceneModel | undefined {
    return this.models.get(id);
  }

  has(id: ModelId): boolean {
    return this.models.has(id);
  }

  setMode(mode: PresentationMode): void {
    this.mode?.exit();
    this.mode = mode;
    mode.enter();
  }

  /**
   * Show or hide the room the model stands in.
   *
   * The floor and the sign only make sense at real scale, so they belong to VR. On a
   * desktop the model is normalised to an arbitrary size and a floor would be a lie.
   *
   * The backdrop goes with them: it is as much a part of the room as the floor is, and
   * this is already the one call that says which of the two places the model is in.
   */
  setEnvironmentVisible(visible: boolean): void {
    this.floor.visible = visible;
    this.sign.visible = visible;
    this.scene.background = visible ? this.roomBackground : this.pageBackground;
  }

  /**
   * Match the canvas to the page around it.
   *
   * Takes a colour rather than a theme, so the scene needs to know nothing about which
   * themes exist or what they are called — only what to paint. The value is mutated in
   * place because `scene.background` already points at it, which saves having to decide
   * whether a VR session is in progress and would be interrupted by the swap.
   */
  setPageBackground(colour: string): void {
    this.pageBackground.set(colour);
  }

  /**
   * Dress the room for a theme.
   *
   * Separate from the page's background, and given a theme rather than a colour, because
   * the room is not trying to match anything on the page — it has its own two looks, and
   * the sign has to be repainted along with the sky it hangs against.
   *
   * Takes effect immediately even mid-session: the sky is mutated in place, so a scene
   * already showing it picks the change up on the next frame.
   */
  setRoomTheme(theme: Theme): void {
    const look = ROOM[theme];
    this.roomBackground.set(look.sky);
    this.sign.setInk(look.ink);
  }

  /**
   * Build a model and put it in the scene, hidden until a mode shows it.
   *
   * Loading and showing are separate because the two modes want different sets: the
   * desktop shows the one selected model, VR shows the room. Keeping a model loaded while
   * it is out of view is what lets a headset user glance back at the desktop and return
   * to a room that is still arranged as they left it.
   *
   * Re-loading an id that is already present returns what is there rather than parsing the
   * file again — the geometry cannot have changed, since editing a model is not something
   * this application does.
   *
   * The centering offset is baked into the geometry rather than applied as a transform on
   * a wrapper object, so the root's transform stays entirely the active mode's to own —
   * which is what makes attaching it to a controller during a grab straightforward.
   */
  async load(
    id: ModelId,
    data: ArrayBuffer,
    stats: ModelStats,
    colour: string = DEFAULT_COLOUR,
  ): Promise<SceneModel> {
    const existing = this.models.get(id);
    if (existing) return existing;

    const object = await this.buildObject(data, stats, colour);

    // Awaiting above means another call for the same id could have finished while this one
    // was waiting on a loader. Two of everything for one model would leave the second in
    // the scene and the first leaked, so the race is settled in favour of whoever got there.
    const arrived = this.models.get(id);
    if (arrived) {
      dispose(object);
      return arrived;
    }

    // Measured from what was built rather than taken from the record, and the two are
    // normally identical — the record was measured from the same file. They come apart
    // when the rule for reading a format changes, and a record written under the old rule
    // would then place and frame a model that no longer exists. Believing the geometry in
    // front of us costs one bounding box and cannot be wrong.
    const bounds = extentOf(object) ?? stats.bounds;

    // Applied to what was built rather than to the group below it, so that the group's
    // transform stays entirely the active mode's to own — which is what makes handing it
    // to a controller during a grab straightforward. Every kind therefore arrives centred
    // on its own footprint whatever axes its format uses.
    const [dx, dy, dz] = computeCenteringOffset(bounds);
    object.position.set(dx, dy, dz);

    const root = new Group();
    root.name = `model:${id}`;
    root.add(object);
    root.visible = false;

    const model: SceneModel = { id, root, object, bounds };
    this.models.set(id, model);
    this.scene.add(root);
    return model;
  }

  /**
   * Turn a file into something drawable, by whatever route its format needs.
   *
   * Most formats become a single mesh: one geometry, one material, and a colour that is
   * either the file's or the user's. G-code goes through the toolpath reader, which
   * rebuilds the printed object from the moves — see `viewer/toolpath.ts`.
   *
   * glTF and 3MF do not. They arrive as a scene of separately shaded parts and are kept
   * that way, because flattening them into one mesh loses the very thing they were chosen
   * to carry: a picture on a face is not a colour, and no amount of per-vertex
   * approximation makes it one on a face of two triangles. So the loader's own meshes,
   * materials and textures are used as they came. Merging still happens, but only to
   * measure the model — see `readModel.ts`.
   *
   * Asynchronous because one of the loaders is. Nothing else here waits on anything.
   */
  private async buildObject(
    data: ArrayBuffer,
    stats: ModelStats,
    colour: string,
  ): Promise<Object3D> {
    if (kindOf(stats) === '3mf' || kindOf(stats) === 'gltf') {
      const scene = await buildScene(data, kindOf(stats) === '3mf' ? '3mf' : 'gltf');
      return shadowed(scene);
    }

    const geometry = this.buildGeometry(data, stats);

    // Only PLY reaches here carrying opacity of its own. STL and OBJ have no way to say
    // anything about it, and a toolpath is a reconstruction rather than a described object.
    const translucent =
      kindOf(stats) === 'ply' && applyPlyAlpha(geometry, readPlyAlpha(data));

    // A file that carries its own colours keeps them: the stored colour is a preference
    // about a model that had none, and applying it here would tint every vertex.
    return hasFileColours(stats)
      ? drawSurface(geometry, '#ffffff', true, translucent)
      : drawSurface(geometry, colour, false, false);
  }

  private buildGeometry(data: ArrayBuffer, stats: ModelStats): BufferGeometry {
    switch (kindOf(stats)) {
      case 'gcode': {
        const reading = readToolpath(
          new TextDecoder('utf-8', { fatal: false }).decode(data),
        );
        if (!reading.ok) throw new Error(reading.message);
        return reading.geometry;
      }
      case 'ply':
        return correctColourScale(new PLYLoader().parse(data), stats);
      case 'obj':
        // No colours: an OBJ keeps them in a separate `.mtl` that a dropped file does not
        // bring with it, so there is never anything to bake.
        return mergeParts(new OBJLoader().parse(new TextDecoder().decode(data)));
      case '3mf':
      default:
        return this.loader.parse(data);
    }
  }

  /**
   * Recolour a model.
   *
   * Cheap enough to call on every pointer move in a colour picker: it writes three floats
   * into an existing material, and the render loop is already running, so the change shows
   * on the next frame with nothing rebuilt.
   *
   * Does nothing for a model that is not loaded, which is the state the picker is hidden
   * in anyway.
   */
  setModelColour(id: ModelId, colour: string): void {
    const model = this.models.get(id);
    if (!model) return;

    model.object.traverse((child) => {
      if (!isMesh(child)) return;

      for (const material of materialsOf(child)) {
        // A part drawn from its own colours refuses to be repainted. The picker is
        // disabled for such a model so nothing should ask, but the rule belongs where the
        // colours are rather than in whichever control happens to be on screen.
        if (material.vertexColors || material.map) continue;
        material.color.set(colour);
      }
    });
  }

  /**
   * Take a model out of the scene and free what it was holding.
   *
   * Whoever calls this owns making sure nothing still refers to it — in particular that
   * no hand is holding it, which `GrabController.setTargets` handles when the room
   * changes.
   */
  unload(id: ModelId): void {
    const model = this.models.get(id);
    if (!model) return;

    this.models.delete(id);
    this.scene.remove(model.root);
    model.root.remove(model.object);
    dispose(model.object);
  }

  /** Take everything out of the scene. */
  unloadAll(): void {
    for (const id of [...this.models.keys()]) this.unload(id);
  }

  /**
   * Show exactly these models and hide the rest.
   *
   * Whose call this is belongs to the active mode: the desktop wants one model and VR
   * wants the room, and both change what they want without anything being loaded or
   * unloaded.
   */
  setVisible(ids: Iterable<ModelId>): void {
    const shown = new Set(ids);
    for (const [id, model] of this.models) model.root.visible = shown.has(id);
  }

  /**
   * Point the key light at what is on show and tighten its shadow frustum around it.
   *
   * Worth doing on every placement change because the two modes work at wildly different
   * scales — roughly a 1-unit model on the desktop against a 2 cm one in VR — and a
   * shadow camera sized for one wastes almost all its resolution on the other.
   *
   * With a roomful, the frustum has to cover all of them at once: one shadow map is what
   * a single directional light gets, so the choice is between covering the room coarsely
   * and lighting one model crisply while the rest cast nothing.
   */
  frameLighting(): void {
    const sphere = this.visibleWorldSphere();
    const radius = Math.max(sphere.radius, 0.25);

    this.keyLight.target.position.copy(sphere.center);
    this.keyLight.target.updateMatrixWorld();

    this.keyLight.position
      .set(0.5, 1.0, 0.75)
      .normalize()
      .multiplyScalar(radius * 4)
      .add(sphere.center);

    const shadow = this.keyLight.shadow.camera;
    shadow.left = -radius * 1.8;
    shadow.right = radius * 1.8;
    shadow.top = radius * 1.8;
    shadow.bottom = -radius * 1.8;
    shadow.near = 0.01;
    shadow.far = radius * 10;
    shadow.updateProjectionMatrix();
  }

  /**
   * World-space bounding sphere of everything on show, or a unit sphere at the origin
   * when nothing is.
   */
  visibleWorldSphere(): Sphere {
    const sphere = new Sphere(new Vector3(), 1);
    const box = new Box3();

    for (const model of this.models.values()) {
      if (!model.root.visible) continue;
      model.root.updateMatrixWorld(true);
      box.union(new Box3().setFromObject(model.object));
    }

    return box.isEmpty() ? sphere : box.getBoundingSphere(sphere);
  }

  resize(): void {
    const { clientWidth, clientHeight } = this.host;
    const width = Math.max(clientWidth, 1);
    const height = Math.max(clientHeight, 1);

    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Start rendering.
   *
   * `setAnimationLoop` rather than `requestAnimationFrame` because WebXR requires the
   * renderer's own loop to drive frames while a session is presenting; it drives the
   * desktop case perfectly well too, so there is only ever one loop.
   */
  start(): void {
    this.renderer.setAnimationLoop((time) => {
      const delta = this.lastFrameTime === 0 ? 0 : (time - this.lastFrameTime) / 1000;
      this.lastFrameTime = time;

      this.mode?.update(delta);
      this.renderer.render(this.scene, this.camera);
    });
  }

  stop(): void {
    this.renderer.setAnimationLoop(null);
  }
}

/**
 * A surface: the usual case, and what three of the four formats produce.
 *
 * Flat shading reads the normals off the geometry's own derivatives, which conveniently
 * sidesteps both the STL exporters that write zeroes for them and the formats that carry
 * none at all. It also looks like an FDM print, which is what most of these models are.
 *
 * The colour is a constructor argument rather than something applied afterwards, so there
 * is no frame in which a model appears in the wrong colour and then corrects itself.
 */
function drawSurface(
  geometry: BufferGeometry,
  colour: string,
  vertexColours: boolean,
  translucent: boolean,
): Mesh<BufferGeometry, MeshStandardMaterial> {
  const mesh = new Mesh(
    geometry,
    new MeshStandardMaterial({
      // three multiplies the two together, so the file's colours only survive intact
      // against white. The caller passes white for exactly that reason.
      color: colour,
      vertexColors: vertexColours,
      roughness: 0.82,
      metalness: 0.02,
      flatShading: true,
      ...translucency(translucent),
    }),
  );

  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * What to add to a material that has to let light through.
 *
 * `depthWrite` is deliberately left on, which three does by default and which most
 * translucent rendering turns off. Off, every surface behind shows through and the order
 * they arrive in decides what you see, which for a model of many overlapping shells is a
 * shifting mess as the camera moves. On, a translucent object hides its own far side —
 * less physically right, and far easier to look at, which is what a preview is for.
 */
function translucency(
  translucent: boolean,
): { transparent: boolean } | Record<never, never> {
  return translucent ? { transparent: true } : {};
}

/**
 * Let every part of a loaded scene take part in the lighting.
 *
 * The shadow a model casts on the floor is most of what sells it as standing there rather
 * than hovering, and a loader's meshes arrive with shadows switched off.
 */
function shadowed(object: Object3D): Object3D {
  object.traverse((child) => {
    if (!isMesh(child)) return;
    child.castShadow = true;
    child.receiveShadow = true;
  });

  return object;
}

/**
 * Free everything a built object holds.
 *
 * Geometries, materials and the textures hanging off them. Textures matter most: a glTF's
 * images are the largest thing it carries by some way, and dropping the reference without
 * disposing leaves them on the graphics card until the context is lost.
 */
function dispose(object: Object3D): void {
  object.traverse((child) => {
    if (!isMesh(child)) return;

    child.geometry.dispose();
    for (const material of materialsOf(child)) {
      // Every texture-shaped property, whatever it was for. Reaching for `map` alone would
      // leave a glTF's normal, occlusion and emissive images behind.
      for (const value of Object.values(material)) {
        if (value instanceof Texture) value.dispose();
      }
      material.dispose();
    }
  });
}

/** A mesh's materials, as a list, since it may carry one or several. */
function materialsOf(
  mesh: Mesh<BufferGeometry, MeshStandardMaterial>,
): readonly MeshStandardMaterial[] {
  const material = mesh.material;
  return Array.isArray(material) ? material : [material];
}

/**
 * Whether this is a mesh, said in a way that keeps its geometry and material types.
 *
 * `instanceof Mesh` on its own narrows to a mesh whose type arguments are unknown, which
 * makes everything reached through it untyped.
 */
function isMesh(child: Object3D): child is Mesh<BufferGeometry, MeshStandardMaterial> {
  return child instanceof Mesh;
}

/**
 * The extent of a built object, before anything has been done to place it.
 *
 * `undefined` for an object holding nothing drawable, where the box comes back empty and
 * its corners are infinities — numbers that would poison every placement they reached.
 */
function extentOf(object: Object3D): Bounds | undefined {
  const box = new Box3().setFromObject(object);
  if (box.isEmpty()) return undefined;

  return {
    min: [box.min.x, box.min.y, box.min.z],
    max: [box.max.x, box.max.y, box.max.z],
  };
}
